/**
 * KraftStories Overlay Manager
 * ---------------------------------------------------------------------------
 * Guarantees that only ONE floating overlay is on screen at any moment.
 *
 * Before this existed, the Klaviyo email popup (8s), the floating video widget
 * (20% scroll) and the lead-magnet slide-in (25s / 40% scroll) each triggered
 * independently and could stack on top of each other in the same corner.
 *
 * Consumers ask permission instead of showing themselves directly:
 *
 *   var ok = KraftOverlays.request('my-overlay', {
 *     priority: 20,          // higher wins when several are waiting
 *     show: function () {}   // called later if it has to wait its turn
 *   });
 *   if (ok) actuallyShowIt();
 *
 * When the overlay is closed, minimized, or converts:
 *   KraftOverlays.release('my-overlay');
 *
 * If the overlay decides it should never appear this visit (suppression cookie,
 * dismissed earlier, etc.):
 *   KraftOverlays.cancel('my-overlay');
 *
 * The active overlay id is mirrored onto <html data-kraft-overlay="..."> so CSS
 * can react without extra JS.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.KraftOverlays) return;

  // Quiet gap after one overlay closes before the next is allowed in.
  var COOLDOWN_MS = 12000;

  var active = null;      // id of the overlay currently owning the screen
  var queue = [];         // [{ id, priority, show }] waiting their turn
  var lastReleaseAt = 0;  // timestamp of the last release()
  var timer = null;

  function markDom() {
    var el = document.documentElement;
    if (!el) return;
    if (active) el.setAttribute('data-kraft-overlay', active);
    else el.removeAttribute('data-kraft-overlay');
  }

  function drop(id) {
    queue = queue.filter(function (item) { return item.id !== id; });
  }

  function pump() {
    if (active || !queue.length) return;

    var wait = COOLDOWN_MS - (Date.now() - lastReleaseAt);
    clearTimeout(timer);
    if (wait > 0) {
      timer = setTimeout(pump, wait);
      return;
    }

    queue.sort(function (a, b) { return b.priority - a.priority; });
    var next = queue.shift();
    active = next.id;
    markDom();
    try {
      next.show();
    } catch (e) {
      active = null;
      markDom();
    }
  }

  window.KraftOverlays = {
    /**
     * Ask for the screen. Returns true when granted immediately — the caller
     * should then show itself. Returns false when it has to wait; opts.show is
     * invoked later once the screen frees up (unless opts.queue === false).
     */
    request: function (id, opts) {
      opts = opts || {};
      if (active === id) return true;

      drop(id);

      if (!active && Date.now() - lastReleaseAt >= COOLDOWN_MS) {
        active = id;
        markDom();
        return true;
      }

      if (opts.queue === false) return false;

      queue.push({
        id: id,
        priority: typeof opts.priority === 'number' ? opts.priority : 0,
        show: typeof opts.show === 'function' ? opts.show : function () {}
      });
      pump();
      return false;
    },

    /** Overlay was closed or minimized — hand the screen to whoever is next. */
    release: function (id) {
      drop(id);
      if (active !== id) return;
      active = null;
      lastReleaseAt = Date.now();
      markDom();
      pump();
    },

    /** Overlay will not appear at all this visit — free the slot with no cooldown. */
    cancel: function (id) {
      drop(id);
      if (active !== id) return;
      active = null;
      markDom();
      pump();
    },

    /** Currently displayed overlay id, or null. */
    current: function () { return active; }
  };
})();
